import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Accordion from '@repo/ui/accordion'
import Button from '@repo/ui/button'
import { FamilyThreeIcon, PrintIcon } from '@repo/ui/icons'
import Input from '@repo/ui/input'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import SelectInput from '@repo/ui/select-input'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import {
  assignHostFamilyToStaff,
  changeStaffHostFamily,
  unassignStaffHostFamily
} from '../../../api/centers/assign-host-family-to-staff'
import { assignMobileNumberToCenter } from '../../../api/centers/assign-mobile-number-to-center'
import { getCenterGroups } from '../../../api/centers/center-groups'
import { getCenter } from '../../../api/centers/get-center'
import { getCenterManager } from '../../../api/centers/get-center-manager'
import { getMobileNumberForFrenchLeaders } from '../../../api/ols/mobile-number-for-french-leaders/get-mobile-number-for-french-leaders'
import { getOlPermissions } from '../../../lib/constants/ol-permissions'
import useAuth from '../../../lib/hooks/use-auth'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { AccomodationEnum } from '../../../lib/types/enums/accomodation-type/accomodation-type'
import { OLTypeEnum } from '../../../lib/types/enums/ol/ol-type.enum'
import { affectedStaffCenterPlacementFields } from '../../../lib/types/fields/affected-staffs/affected-staff'
import { AccordionState } from '../../../lib/types/utils'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { formatPhoneNumber } from '../../../utils/functions/format-phone-number'
import OlCenterAffectedStaffTable from '../ol-common/ol-center-affected-staff-table'
import OlCenterAssignHostFamilyForm from '../ol-common/ol-center-placement-enrollment-table/ol-center-assign-host-family-form'
import OlCenterChangeHostFamilyForm from '../ol-common/ol-center-placement-enrollment-table/ol-center-change-host-family-form'
import OlCenterPlacementSummaryBadges, {
  OlCenterPlacementSummaryBadgesRef
} from '../ol-common/ol-center-placement-summary-badges'
import OlPersonalSpaceCenterAdminComments from './ol-personal-space-center-comments/ol-personal-space-center-admin-comments'
import OlPersonalSpaceCommentSection from './ol-personal-space-center-comments/ol-personal-space-comment-section'
import OlPersonalSpaceCenterStudentDataTable from './ol-personal-space-center-student-data-table'

type Props = {
  id: number
}

type Key =
  | 'ol-personal-space-staffs-data'
  | 'ol-personal-space-students-data'
  | 'ol-personal-space-comments'

const initialAccordionState = (
  t: TFunction<AvailableLanguages, undefined>
): AccordionState<Key>[] => [
  { key: 'ol-personal-space-staffs-data', label: t('ol.center.staff.table.title'), open: true },
  {
    key: 'ol-personal-space-students-data',
    label: t('ol.center.enrollment.table.title'),
    open: true
  },
  {
    key: 'ol-personal-space-comments',
    label: t('ol.center.enrollment.table.comment.title'),
    open: true
  }
]

const OlPersonalSpaceCenterPlacement = ({ id }: Props) => {
  const { t } = useTranslate()
  const { downloadDocuments } = useDownloadDocuments()
  const [accordionState, setAccordionState] = useState<AccordionState<Key>[]>(
    initialAccordionState(t)
  )
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const olId = user?.ol?.id

  const summaryBadgesRef = useRef<OlCenterPlacementSummaryBadgesRef>(null)

  const refetchAffectedStaffs = () => {
    queryClient.invalidateQueries({ queryKey: ['affected-staffs', 'ol-center-table', id] })
    queryClient.invalidateQueries({ queryKey: ['center-manager', id] })
  }

  const { toggleModal, modalState, selectedData, closeModel } = useModalManager<
    'assign-host-family-to-staff' | 'change-host-family-to-staff',
    { id: number }
  >(['assign-host-family-to-staff', 'change-host-family-to-staff'])

  const { data: center } = useQuery({
    queryKey: ['centers', id, 'FULL'],
    queryFn: () => {
      if (!id || isNaN(id)) return
      return getCenter(+id, 'FULL')
    }
  })

  const { data: centerGroups } = useQuery({
    queryKey: ['center-groups', id],
    queryFn: () => getCenterGroups(id)
  })
  const hasCenterGroups = (centerGroups?.total ?? 0) > 0

  const { data: centerManager } = useQuery({
    queryKey: ['center-manager', id, affectedStaffCenterPlacementFields],
    queryFn: () => getCenterManager(id, affectedStaffCenterPlacementFields),
    enabled: !!id && !isNaN(id)
  })

  const olPermissions = getOlPermissions({ role: user?.role, type: center?.ol?.type })

  const { data: mobileNumberForFrenchLeaders } = useQuery({
    queryKey: ['mobile-number-for-french-leaders'],
    queryFn: () => {
      if (!olId) return
      return getMobileNumberForFrenchLeaders({ olId })
    },
    enabled: !!olId
  })

  const assignNumberForFrenchLeaders = useMutation({
    mutationFn: assignMobileNumberToCenter,
    mutationKey: ['assign-mobile-number-for-french-leaders'],
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Mobile number assigned successfully!')
      queryClient.invalidateQueries({ queryKey: ['centers', id] })
    }
  })

  const assignHostFamilyToStaffMutation = useMutation({
    mutationFn: assignHostFamilyToStaff,
    mutationKey: ['assign-host-family-to-staff'],
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Host family assigned successfully!')
      refetchAffectedStaffs()
      summaryBadgesRef.current?.refetch()
    }
  })

  const unassignStaffHostFamilyMutation = useMutation({
    mutationFn: unassignStaffHostFamily,
    mutationKey: ['unassign-host-family-to-staff'],
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Host family unassigned successfully!')
      refetchAffectedStaffs()
      summaryBadgesRef.current?.refetch()
    }
  })

  const changeHostFamilyToStaffMutation = useMutation({
    mutationFn: changeStaffHostFamily,
    mutationKey: ['change-host-family-to-staff'],
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Host family changed successfully!')
      refetchAffectedStaffs()
      summaryBadgesRef.current?.refetch()
    }
  })

  const renderAccordionContent = (key: Key) => {
    if (!center) return null
    switch (key) {
      case 'ol-personal-space-staffs-data':
        return (
          <OlCenterAffectedStaffTable
            centerId={id}
            mode="french-view"
            accomodation={center.accomodation}
            {...(center.accomodation === AccomodationEnum.HOST_FAMILY &&
              olPermissions['host-family'] && {
                extraColumns: [
                  {
                    accessorKey: 'id',
                    header: '',
                    cell: ({ row }) => {
                      if (!row.original.staff) return '-'
                      const hasHostFamily = !!row.original.hostFamily
                      return (
                        <Button
                          type="button"
                          label={hasHostFamily ? 'Change host family' : 'Assign host family'}
                          variant={hasHostFamily ? 'secondary' : 'ghost'}
                          className="mr-3"
                          onClick={() => {
                            if (hasHostFamily)
                              toggleModal('change-host-family-to-staff', { id: row.original.id })
                            else
                              toggleModal('assign-host-family-to-staff', { id: row.original.id })
                          }}
                        />
                      )
                    }
                  }
                ]
              })}
          />
        )
      case 'ol-personal-space-students-data':
        return (
          center && (
            <OlPersonalSpaceCenterStudentDataTable
              data={center}
              olId={olId}
              onChangeHostFamilySuccess={() => summaryBadgesRef.current?.refetch()}
            />
          )
        )
      case 'ol-personal-space-comments':
        return (
          <>
            <OlPersonalSpaceCenterAdminComments id={Number(id)} />
            <div className={'h-4'} />
            <OlPersonalSpaceCommentSection id={id} />
          </>
        )
      default:
        return null
    }
  }

  const selectedNumber = mobileNumberForFrenchLeaders?.data.find(
    (number) => number.id === center?.mobileNumberForFrenchLeader?.id
  )
  const mobileNumberOptions =
    mobileNumberForFrenchLeaders?.data.map((number) => ({
      value: number.id,
      label: formatPhoneNumber(number.phoneNumber)
    })) || []
  const hasMobileNumberOptions = mobileNumberOptions.length > 0
  const fallbackPhoneNumber = centerManager?.facNumber?.trim()
  const canSelectMobileNumber =
    !fallbackPhoneNumber &&
    hasMobileNumberOptions &&
    olPermissions['edit-mobile-number-for-french-leaders']

  return (
    <div className="flex flex-col gap-3 mt-4">
      <div className="flex  items-center gap-2">
        <Typography variant="label-medium" color={tailwindColors.black.DEFAULT}>
          {t('ol.center.print.the.students.list')}
        </Typography>
        <Button
          type="button"
          icon={PrintIcon}
          variant="ghost"
          label={t('ol.center.print.global.list')}
          onClick={() => {
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printFullListOfEnrollments.url, {
                id
              })
            })
          }}
        />
        <Button
          type="button"
          icon={PrintIcon}
          variant="ghost"
          label={t('ol.center.print.list.by.proram')}
          onClick={() => {
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printPdfListOfEnrollmentsListByProgram.url, {
                id
              })
            })
          }}
        />
        <Button
          type="button"
          icon={PrintIcon}
          variant="ghost"
          label={t('ol.center.print.list.by.transport.type')}
          onClick={() => {
            downloadDocuments({
              endpoint: constructApiUrl(API.centers.printEnrollmentsListByMeansOfTransport.url, {
                id
              })
            })
          }}
        />
        {hasCenterGroups && (
          <Button
            type="button"
            icon={PrintIcon}
            variant="ghost"
            label={t('ol.center.print.list.by.sub.group')}
            onClick={() => {
              downloadDocuments({
                endpoint: constructApiUrl(
                  API.centers.printPdfListOfEnrollmentsListBySubGroup.url,
                  { id }
                )
              })
            }}
          />
        )}
      </div>
      {center && (
        <Typography variant="sub-heading" color={tailwindColors.black.DEFAULT}>
          {center.ol?.type === OLTypeEnum.RESIDENCE
            ? t('ol.center.placement.diffusion.title.residence')
            : t('ol.center.placement.diffusion.title')}
        </Typography>
      )}

      <div className="flex justify-between items-center">
        <div>
          {center?.accomodation === AccomodationEnum.HOST_FAMILY && (
            <OlCenterPlacementSummaryBadges
              ref={summaryBadgesRef}
              id={id}
              isEnrollmentDiffused={center.enrollmentsDiffusedToOl}
            />
          )}
        </div>
        <div>
          {canSelectMobileNumber ? (
            <SelectInput
              className="w-72"
              label={t('ol.center.placement.diffusion.phone.number')}
              options={mobileNumberOptions}
              selectedOption={
                selectedNumber?.phoneNumber && selectedNumber?.id !== undefined
                  ? {
                      label: formatPhoneNumber(selectedNumber.phoneNumber),
                      value: selectedNumber.id
                    }
                  : undefined
              }
              onChange={(value) => {
                if (!value) return
                assignNumberForFrenchLeaders.mutate({
                  centerId: id,
                  mobileNumberForFrenchLeaderId: value
                })
              }}
            />
          ) : (
            <Input
              readOnly
              type="text"
              className="w-72"
              label={t('ol.center.placement.diffusion.phone.number')}
              value={fallbackPhoneNumber ? formatPhoneNumber(fallbackPhoneNumber) : '-'}
            />
          )}
        </div>
      </div>

      {Object.values(accordionState).map(({ key, open, label }) => (
        <Accordion
          key={key}
          title={label}
          open={open}
          className="bg-white-hover"
          variant="primary"
          onToggle={() =>
            setAccordionState((prev) =>
              prev.map((item) => (item.key === key ? { ...item, open: !item.open } : item))
            )
          }
        >
          {renderAccordionContent(key)}
        </Accordion>
      ))}

      {olId && selectedData && (
        <Modal
          isOpen={modalState['assign-host-family-to-staff']}
          title={t('ol.center.assign.host.family')}
          icon={FamilyThreeIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('assign-host-family-to-staff')}
        >
          <OlCenterAssignHostFamilyForm
            olId={olId}
            className={MODAL_CONTENT_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel() {
                closeModel('assign-host-family-to-staff')
              },
              confirmText: t('button.assign'),
              cancelText: t('button.close'),
              onConfirm: async (data) => {
                await assignHostFamilyToStaffMutation.mutateAsync({
                  centerId: id,
                  id: selectedData.id,
                  hostFamilyId: data.hostFamilyId
                })
                closeModel('assign-host-family-to-staff')
              }
            })}
          />
        </Modal>
      )}

      {olId && selectedData && (
        <Modal
          isOpen={modalState['change-host-family-to-staff']}
          title={t('ol.center.assign.new.host.family.to.staff')}
          icon={FamilyThreeIcon}
          size="medium"
          hideActions
          onCancel={() => closeModel('change-host-family-to-staff')}
        >
          <OlCenterChangeHostFamilyForm
            olId={olId}
            className={MODAL_CONTENT_CLASSNAME}
            buttonsConfig={modalButtonConfig({
              onCancel() {
                closeModel('change-host-family-to-staff')
              },
              confirmText: t('button.assign'),
              cancelText: t('button.close'),
              onConfirm: async (data) => {
                if (!data.hostFamilyId)
                  await unassignStaffHostFamilyMutation.mutateAsync({
                    centerId: id,
                    reasonForChange: data.reasonForChange,
                    id: selectedData.id
                  })
                else
                  await changeHostFamilyToStaffMutation.mutateAsync({
                    ...data,
                    centerId: id,
                    id: selectedData.id
                  })
                closeModel('change-host-family-to-staff')
              }
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default OlPersonalSpaceCenterPlacement
